import { useMemo, useState } from 'react';
import { cn } from '@/lib/utils';
import { Check, ChevronDown } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { FloatingSelectField } from '@/components/ui/floating-input';
import { POKEMON_LANGUAGES } from '@/constants/pokemonLanguages';

interface LanguageMultiSelectFieldProps {
  value: string[];
  onChange: (value: string[]) => void;
  label?: string;
  placeholder?: string;
}

/**
 * Modal-style language multi-select that matches the floating-field inputs
 * used in ItemFormModal (e.g. Overall Condition, Game, Category).
 * Do NOT use this in filter trays — use LanguageMultiSelectSection there.
 */
export function LanguageMultiSelectField({
  value,
  onChange,
  label = 'Language',
  placeholder = 'All Languages',
}: LanguageMultiSelectFieldProps) {
  const [open, setOpen] = useState(false);

  const toggle = (lang: string) => {
    onChange(value.includes(lang) ? value.filter((l) => l !== lang) : [...value, lang]);
  };

  const displayText = useMemo(() => {
    if (value.length === 0) return placeholder;
    if (value.length === 1) {
      const lang = POKEMON_LANGUAGES.find((l) => l.value === value[0]);
      return lang ? `${lang.flag} ${lang.label}` : value[0];
    }
    return `${value.length} languages selected`;
  }, [value, placeholder]);

  return (
    <FloatingSelectField label={label} hasValue={true}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            role="combobox"
            aria-expanded={open}
            data-state={open ? 'open' : 'closed'}
            data-floating-trigger="true"
            className={cn(
              'group flex h-14 w-full items-center justify-between gap-3 rounded-lg field-surface field-border px-3 pt-5 pb-1.5 text-base ring-offset-background focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1 [&>span]:text-left',
              'focus:border-white/30 data-[state=open]:border-white/40 transition-colors'
            )}
          >
            <span className="truncate">{displayText}</span>
            <ChevronDown className="h-4 w-4 opacity-50 group-hover:opacity-100 group-hover:text-white group-data-[state=open]:opacity-100 group-data-[state=open]:text-white transition-all" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] p-0 z-[60] dropdown-surface border-white/[0.08] shadow-lg max-h-[var(--radix-popover-content-available-height)] flex flex-col"
          align="start"
          side="bottom"
          sideOffset={4}
          avoidCollisions={false}
          onTouchStart={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
          onWheel={(e) => e.stopPropagation()}
        >
          <Command>
            <CommandInput placeholder="Search languages..." />
            <CommandList className="max-h-[250px] overflow-y-auto overscroll-contain touch-pan-y [-webkit-overflow-scrolling:touch]">
              <CommandEmpty>No language found.</CommandEmpty>
              <CommandGroup>
                {POKEMON_LANGUAGES.map((lang) => (
                  <CommandItem
                    key={lang.value}
                    value={lang.label}
                    onSelect={() => toggle(lang.value)}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        value.includes(lang.value) ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    <span className="mr-2">{lang.flag}</span>
                    <span className="flex-1">{lang.label}</span>
                    {lang.region && (
                      <span className="text-muted-foreground text-xs">({lang.region})</span>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </FloatingSelectField>
  );
}
